/*!
 * Custom Reviews — custom-designed review section powered by Judge.me data.
 *
 * Data sources (no review content is hard-coded anywhere):
 *   1. product.metafields.judgeme.review_widget_data — JSON Judge.me syncs to Shopify (first page)
 *   2. product.metafields.judgeme.widget             — legacy widget HTML (fallback)
 *   3. Judge.me's storefront review feed (tokenless, the same feed Judge.me's own widget uses)
 *      for further pages, sorting, filtering and search.
 *
 * Judge.me can return two response shapes from the feed and this file handles both:
 *   - New Review Widget enabled: JSON { reviews, histogram, pagination, photo_gallery, ai_summary_text, ... }
 *   - Legacy widget:             JSON { html, page, total_count }
 *
 * If Judge.me ever renames a query parameter, override it without editing this file:
 *   <script>window.CustomReviewsParams = { rating: 'filter_rating' };</script>
 *   <script>window.CustomReviewsSorts  = { latest: { sort_by: 'created_at', sort_dir: 'desc' } };</script>
 * Filters keep working even if the feed ignores a parameter: the script detects it and
 * scans pages on the client instead (see Controller#loadScan).
 */
(function () {
  'use strict';

  if (window.CustomReviews) {
    window.CustomReviews.initAll();
    return;
  }

  /* ------------------------------------------------------------------ *
   * Feed parameter names (overridable)
   * ------------------------------------------------------------------ */
  var PARAMS = Object.assign(
    {
      shop: 'shop_domain',
      url: 'url',
      platform: 'platform',
      product: 'product_id',
      page: 'page',
      perPage: 'per_page',
      sortBy: 'sort_by',
      sortDir: 'sort_dir',
      rating: 'filter_rating',
      search: 'search'
    },
    window.CustomReviewsParams || {}
  );

  var SORTS = Object.assign(
    {
      latest: { sort_by: 'created_at', sort_dir: 'desc' },
      oldest: { sort_by: 'created_at', sort_dir: 'asc' },
      highest: { sort_by: 'rating', sort_dir: 'desc' },
      lowest: { sort_by: 'rating', sort_dir: 'asc' },
      helpful: { sort_by: 'most_helpful', sort_dir: 'desc' },
      pictures: { sort_by: 'pictures_first', sort_dir: 'desc' }
    },
    window.CustomReviewsSorts || {}
  );

  /* Judge.me connection details — not section settings, since the client never needs to change them. */
  var DEFAULTS = {
    feedUrl: 'https://cdn.judge.me/reviews/reviews_for_widget',
    maxScanPages: 15,
    hideNative: true,
    recommendKeyword: 'recommend',
    nativeWidget: '.jdgm-review-widget, .jdgm-widget.jdgm-review-widget, .jdgm-widget-revamp',
    nativeTrigger: ".jdgm-write-rev-link, [class*='write-review'] button, button[class*='write-review'], a[class*='write-review']"
  };

  /* ------------------------------------------------------------------ *
   * Utilities
   * ------------------------------------------------------------------ */
  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function num(value) {
    if (value === null || value === undefined || value === '') return NaN;
    var n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    return isFinite(n) ? n : NaN;
  }

  function first() {
    for (var i = 0; i < arguments.length; i++) {
      var v = arguments[i];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
  }

  function textOf(root, selector) {
    var el = root && root.querySelector(selector);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function initialsOf(name) {
    var parts = String(name || '').replace(/[^\p{L}\p{N}\s'-]/gu, ' ').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    var a = parts[0].charAt(0);
    var b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (a + b).toUpperCase();
  }

  function lang() {
    return document.documentElement.lang || navigator.language || 'en';
  }

  function formatNumber(n, maxFraction) {
    try {
      return Number(n).toLocaleString(lang(), { maximumFractionDigits: maxFraction || 0 });
    } catch (e) {
      return String(n);
    }
  }

  function formatDate(iso, relative) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d)) return esc(iso);
    if (relative && typeof Intl !== 'undefined' && Intl.RelativeTimeFormat) {
      var rtf = new Intl.RelativeTimeFormat(lang(), { numeric: 'auto' });
      var diff = (d.getTime() - Date.now()) / 1000;
      var units = [
        ['year', 31536000],
        ['month', 2592000],
        ['week', 604800],
        ['day', 86400],
        ['hour', 3600],
        ['minute', 60]
      ];
      for (var i = 0; i < units.length; i++) {
        if (Math.abs(diff) >= units[i][1]) return rtf.format(Math.round(diff / units[i][1]), units[i][0]);
      }
      return rtf.format(0, 'minute');
    }
    return d.toLocaleDateString(lang(), { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /** Very small allow-list sanitizer for review/reply HTML coming from Judge.me. */
  var ALLOWED = { P: 1, BR: 1, B: 1, STRONG: 1, I: 1, EM: 1, U: 1, UL: 1, OL: 1, LI: 1, A: 1, SPAN: 1, DIV: 1 };
  var DROP = { SCRIPT: 1, STYLE: 1, IFRAME: 1, OBJECT: 1, EMBED: 1, TEMPLATE: 1, NOSCRIPT: 1, SVG: 1, FORM: 1, INPUT: 1, BUTTON: 1 };
  function sanitize(html) {
    if (!html) return '';
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html);
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 8) return child.remove();
        if (child.nodeType !== 1) return;
        var tag = child.tagName.toUpperCase();
        if (DROP[tag]) return child.remove();
        walk(child);
        if (!ALLOWED[tag]) {
          child.replaceWith.apply(child, Array.prototype.slice.call(child.childNodes));
          return;
        }
        var href = tag === 'A' ? child.getAttribute('href') : null;
        Array.prototype.slice.call(child.attributes).forEach(function (attr) {
          child.removeAttribute(attr.name);
        });
        if (tag === 'A') {
          if (href && /^https?:\/\//i.test(href)) {
            child.setAttribute('href', href);
            child.setAttribute('target', '_blank');
            child.setAttribute('rel', 'nofollow noopener noreferrer');
          }
        }
      });
    })(tpl.content);
    return tpl.innerHTML.trim();
  }

  /** Resolves with the first truthy value of check(elapsedMs), polling until timeout. */
  function waitFor(check, timeout) {
    var start = Date.now();
    return new Promise(function (resolve, reject) {
      (function poll() {
        var value;
        try { value = check(Date.now() - start); } catch (e) { value = null; }
        if (value) return resolve(value);
        if (Date.now() - start >= timeout) return reject(new Error('timeout'));
        setTimeout(poll, 150);
      })();
    });
  }

  function plainText(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = String(html || '');
    return (tpl.content.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function starsHTML(rating, sizeClass, max) {
    max = max || 5;
    var r = num(rating);
    if (!isFinite(r)) r = 0;
    var pct = Math.max(0, Math.min(100, (r / max) * 100));
    return (
      '<span class="cr__stars ' + (sizeClass || '') + '" role="img" aria-label="' + esc(formatNumber(r, 2)) + ' / ' + max + '">' +
      '<span class="cr__stars-fill" style="width:' + pct.toFixed(2) + '%"></span></span>'
    );
  }

  /* ------------------------------------------------------------------ *
   * Normalisers — turn Judge.me data into one internal shape
   * ------------------------------------------------------------------ */
  var NOT_VERIFIED = { '': 1, 'false': 1, '0': 1, 'no': 1, 'nothing': 1, 'none': 1, 'not_verified': 1, 'unverified': 1 };

  function isVerified(r) {
    var v = String(first(r.verified_buyer, r.verified, '')).toLowerCase().trim();
    if (!NOT_VERIFIED[v] && v !== 'email' && v !== 'admin') return true;
    var badges = r.transparency_badges || [];
    return badges.some(function (b) {
      var key = typeof b === 'string' ? b : (b && (b.type || b.key || b.name)) || '';
      return /verified[_\s-]?buyer|buyer/i.test(key);
    });
  }

  function normalizeAnswer(a) {
    if (!a) return null;
    var value = first(a.value, a.answer, a.answer_value);
    if (Array.isArray(value)) value = value.join(', ');
    return {
      id: first(a.cf_question_id, a.question_id, a.id),
      title: first(a.question_title, a.title, a.label, '') + '',
      type: (first(a.question_type, a.type, '') + '').toLowerCase(),
      value: value == null ? '' : String(value),
      lower: first(a.lower_value, a.lower_label, a.min_label, ''),
      upper: first(a.top_value, a.upper_value, a.top_label, a.max_label, '')
    };
  }

  function isScale(a) {
    if (!a) return false;
    if (!isFinite(num(a.value))) return false;
    return !!(a.lower || a.upper || /scale|slider|range|rating/.test(a.type));
  }

  function normalizeV3Review(r) {
    if (!r) return null;
    var author = first(r.reviewer_name, r.reviewer && r.reviewer.name, r.name, '');
    var pics = (r.pictures_urls || r.pictures || []).map(function (p) {
      var u = p && (p.urls || p);
      if (typeof u === 'string') return { thumb: u, full: u };
      return { thumb: first(u.compact, u.small, u.original, u.huge), full: first(u.huge, u.original, u.compact) };
    }).filter(function (p) { return p.thumb; });
    var videos = (r.video_external_ids || []).filter(Boolean).map(function (id) { return { vimeo: String(id) }; });
    return {
      id: String(first(r.uuid, r.id, Math.random().toString(36).slice(2))),
      author: author,
      initials: first(r.reviewer_all_initials, initialsOf(author), ''),
      verified: isVerified(r),
      location: first(r.location, ''),
      rating: num(r.rating),
      title: first(r.title, ''),
      bodyHtml: sanitize(first(r.body_html, r.body, '')),
      createdAt: first(r.created_at, r.published_at, ''),
      productTitle: first(r.product_title, r.product && r.product.title, ''),
      variant: first(r.product_variant_title, r.variant_title, ''),
      answers: (r.cf_answers || r.custom_form_answers || []).map(normalizeAnswer).filter(Boolean),
      pictures: pics,
      videos: videos,
      reply: r.reply_content ? { name: first(r.shop_reply_name, ''), html: sanitize(r.reply_content) } : null,
      up: num(first(r.thumb_up, r.thumb_up_count, 0)) || 0,
      down: num(first(r.thumb_down, r.thumb_down_count, 0)) || 0
    };
  }

  function normalizeLegacyReview(el) {
    var author = textOf(el, '.jdgm-rev__author');
    var pictures = Array.prototype.slice.call(el.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__pic-img')).map(function (img) {
      var thumb = img.getAttribute('data-src') || img.getAttribute('src');
      var link = img.closest('a');
      return { thumb: thumb, full: (link && link.getAttribute('href')) || img.getAttribute('data-original') || thumb };
    }).filter(function (p, i, all) {
      return p.thumb && !/^data:image\/gif/.test(p.thumb) && all.findIndex(function (q) { return q.thumb === p.thumb; }) === i;
    });
    var videos = [];
    Array.prototype.slice.call(el.querySelectorAll('.jdgm-rev__vids [data-external-id], .jdgm-rev__vids iframe, [data-video-id]')).forEach(function (v) {
      var id = v.getAttribute('data-external-id') || v.getAttribute('data-video-id');
      if (!id && v.src) {
        var m = v.src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
        id = m && m[1];
      }
      if (id && !videos.some(function (x) { return x.vimeo === id; })) videos.push({ vimeo: id });
    });
    var answers = Array.prototype.slice.call(el.querySelectorAll('.jdgm-rev__cf-ans')).map(function (a) {
      var valueEl = a.querySelector('[data-value]');
      return normalizeAnswer({
        title: textOf(a, '.jdgm-rev__cf-ans__title').replace(/:\s*$/, ''),
        value: first(a.getAttribute('data-value'), valueEl && valueEl.getAttribute('data-value'), textOf(a, '.jdgm-rev__cf-ans__value')),
        type: a.getAttribute('data-type') || (a.className.match(/--(\w+)/) || [])[1],
        lower_value: first(a.getAttribute('data-lower-value'), textOf(a, '[class*="lower"], [class*="__min"]')),
        top_value: first(a.getAttribute('data-top-value'), textOf(a, '[class*="__top"], [class*="upper"], [class*="__max"]'))
      });
    });
    var replyEl = el.querySelector('.jdgm-rev__reply-content');
    var ratingEl = el.querySelector('.jdgm-rev__rating');
    var timeEl = el.querySelector('.jdgm-rev__timestamp');
    var bodyEl = el.querySelector('.jdgm-rev__body');
    return {
      id: el.getAttribute('data-review-id') || Math.random().toString(36).slice(2),
      author: author,
      initials: initialsOf(author),
      verified: el.getAttribute('data-verified-buyer') === 'true' || !!el.querySelector('.jdgm-rev__buyer-badge'),
      location: textOf(el, '.jdgm-rev__location'),
      rating: num(ratingEl && ratingEl.getAttribute('data-score')),
      title: textOf(el, '.jdgm-rev__title'),
      bodyHtml: sanitize(bodyEl ? bodyEl.innerHTML : ''),
      createdAt: (timeEl && timeEl.getAttribute('data-content')) || '',
      productTitle: el.getAttribute('data-product-title') || textOf(el, '.jdgm-rev__prod-link'),
      variant: textOf(el, '.jdgm-rev__variant-title, .jdgm-rev__product-variant'),
      answers: answers.filter(function (a) { return a && a.title; }),
      pictures: pictures,
      videos: videos,
      reply: replyEl ? { name: textOf(el, '.jdgm-rev__replier'), html: sanitize(replyEl.innerHTML) } : null,
      up: num(el.getAttribute('data-thumb-up-count')) || 0,
      down: num(el.getAttribute('data-thumb-down-count')) || 0
    };
  }

  function parseLegacyHTML(html) {
    var doc = new DOMParser().parseFromString('<div id="cr-root">' + (html || '') + '</div>', 'text/html');
    var root = doc.getElementById('cr-root');
    var widg = root.querySelector('.jdgm-rev-widg');
    var histogram = Array.prototype.slice.call(root.querySelectorAll('.jdgm-histogram__row')).map(function (row) {
      return {
        rating: num(row.getAttribute('data-rating')),
        frequency: num(row.getAttribute('data-frequency')),
        percentage: num(row.getAttribute('data-percentage'))
      };
    }).filter(function (h) { return isFinite(h.rating); });
    var paginate = root.querySelector('.jdgm-paginate');
    return {
      reviews: Array.prototype.slice.call(root.querySelectorAll('.jdgm-rev')).map(normalizeLegacyReview),
      average: widg ? num(widg.getAttribute('data-average-rating')) : NaN,
      count: widg ? num(widg.getAttribute('data-number-of-reviews')) : NaN,
      histogram: histogram,
      perPage: paginate ? num(paginate.getAttribute('data-per-page')) : NaN
    };
  }

  function summaryAttributes(list) {
    if (!Array.isArray(list)) return [];
    return list.map(function (q) {
      if (!q || typeof q !== 'object') return null;
      var avg = num(first(q.average, q.average_value, q.avg, q.average_rating, q.average_score, q.mean, q.value));
      if (!isFinite(avg)) return null;
      return {
        id: first(q.cf_question_id, q.question_id, q.id),
        title: first(q.question_title, q.title, q.label, q.name, '') + '',
        value: avg,
        lower: first(q.lower_value, q.lower_label, q.min_label, ''),
        upper: first(q.top_value, q.upper_value, q.top_label, q.max_label, ''),
        points: num(first(q.scale, q.max, q.max_value, q.points, q.number_of_points, q.scale_max))
      };
    }).filter(function (a) { return a && a.title; });
  }

  /** Accepts any Judge.me payload (feed response or metafield) and returns one shape. */
  function parsePayload(json) {
    if (!json) return null;
    // The metafield may hold a JSON object or a JSON string — unwrap up to two levels.
    for (var i = 0; i < 2 && typeof json === 'string'; i++) {
      try { json = JSON.parse(json); } catch (e) { return null; }
    }
    if (!json || typeof json !== 'object') return null;
    if (Array.isArray(json.reviews)) {
      var pg = json.pagination || {};
      var aiTranslations = json.ai_summary_translations || {};
      var l = lang().toLowerCase();
      return {
        kind: 'v3',
        reviews: json.reviews.map(normalizeV3Review).filter(Boolean),
        average: num(json.average_rating),
        count: num(json.number_of_reviews),
        histogram: (json.histogram || []).map(function (h) {
          return { rating: num(h.rating), frequency: num(h.frequency), percentage: num(h.percentage) };
        }),
        attributes: summaryAttributes(json.custom_form_filters_and_averages),
        ai: first(aiTranslations[l], aiTranslations[l.split('-')[0]], json.ai_summary_text, ''),
        gallery: (json.photo_gallery || []).map(normalizeV3Review).filter(Boolean),
        page: num(pg.current_page),
        totalPages: num(pg.total_pages),
        perPage: num(pg.per_page),
        sortKey: json.sort_key || ''
      };
    }
    if (typeof json.html === 'string') {
      var legacy = parseLegacyHTML(json.html);
      var total = num(first(json.total_count, legacy.count));
      var per = isFinite(legacy.perPage) ? legacy.perPage : legacy.reviews.length;
      return {
        kind: 'legacy',
        reviews: legacy.reviews,
        average: legacy.average,
        count: legacy.count,
        histogram: legacy.histogram,
        attributes: [],
        ai: '',
        gallery: [],
        page: num(json.page),
        totalPages: isFinite(total) && per ? Math.ceil(total / per) : NaN,
        perPage: per,
        total: total,
        sortKey: ''
      };
    }
    return null;
  }

  /* ------------------------------------------------------------------ *
   * Controller (one per section on the page)
   * ------------------------------------------------------------------ */
  function Controller(root) {
    this.root = root;
    root.__customReviews = this;
    var cfgEl = root.querySelector('[data-cr-config]');
    try {
      this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}');
    } catch (e) {
      this.cfg = {};
      console.error('[Custom Reviews] Invalid config JSON', e);
    }
    this.cfg = Object.assign({}, DEFAULTS, this.cfg);
    this.L = this.cfg.labels || {};
    this.el = {};
    var self = this;
    [
      'average', 'average-stars', 'count', 'histogram', 'attributes', 'ai', 'ai-text', 'gallery', 'gallery-track',
      'gallery-prev', 'gallery-next', 'toolbar', 'search', 'sort', 'rating', 'media', 'status', 'list', 'more',
      'write', 'lightbox', 'lightbox-media', 'lightbox-review', 'lightbox-close', 'lightbox-prev', 'lightbox-next'
    ].forEach(function (key) {
      self.el[key.replace(/-(\w)/g, function (_, c) { return c.toUpperCase(); })] = root.querySelector('[data-cr-' + key + ']');
    });

    this.state = {
      sort: 'latest',
      rating: '',
      media: false,
      search: '',
      reviews: [],
      ids: Object.create(null),
      serverPage: 0,
      totalPages: NaN,
      perPage: num(this.cfg.perPage) || 0,
      done: false,
      scan: false,
      loading: false,
      sortVerified: false,
      clientSort: false
    };
    this.summary = null;
    this.galleryItems = [];
    this.requestId = 0;

    this.initial = this.readInitial();
    this.bind();
    this.hideNativeWidgets();
    this.start();
  }

  Controller.prototype.readInitial = function () {
    var data = null;
    var jsonEl = this.root.querySelector('[data-cr-initial]');
    var raw = jsonEl ? (jsonEl.getAttribute('data-cr-initial') || jsonEl.textContent || '').trim() : '';
    if (raw) data = parsePayload(raw);
    if (!data || !data.reviews) {
      var legacyEl = this.root.querySelector('[data-cr-legacy]');
      if (legacyEl) {
        var html = legacyEl.innerHTML || (legacyEl.content && Array.prototype.map.call(legacyEl.content.childNodes, function (n) {
          return n.outerHTML || n.textContent;
        }).join(''));
        if (html && html.trim()) data = parsePayload({ html: html });
      }
    }
    return data;
  };

  /* ---------- Networking ---------- */
  Controller.prototype.buildUrl = function (page, opts) {
    var cfg = this.cfg;
    var u = new URL(cfg.feedUrl || 'https://cdn.judge.me/reviews/reviews_for_widget');
    var p = u.searchParams;
    p.set(PARAMS.shop, cfg.shopDomain);
    if (PARAMS.url) p.set(PARAMS.url, cfg.shopDomain);
    p.set(PARAMS.platform, 'shopify');
    p.set(PARAMS.product, cfg.productId);
    p.set(PARAMS.page, page);
    if (this.state.perPage) p.set(PARAMS.perPage, this.state.perPage);
    var sort = SORTS[this.state.sort];
    // Keep Judge.me's dashboard default order until the shopper picks a sort, if we can't map it.
    if (sort && (this.sortTouched || !this.initialSortUnknown)) {
      p.set(PARAMS.sortBy, sort.sort_by);
      p.set(PARAMS.sortDir, sort.sort_dir);
    }
    if (!opts || !opts.unfiltered) {
      if (this.state.rating) p.set(PARAMS.rating, this.state.rating);
      if (this.state.search) p.set(PARAMS.search, this.state.search);
    }
    return u.toString();
  };

  Controller.prototype.fetchPage = function (page, opts) {
    var url = this.buildUrl(page, opts);
    if (this.abort) this.abort.abort();
    this.abort = typeof AbortController !== 'undefined' ? new AbortController() : null;
    return fetch(url, { credentials: 'omit', signal: this.abort ? this.abort.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('Judge.me feed responded ' + res.status);
        return res.json();
      })
      .then(function (json) {
        var data = parsePayload(json);
        if (!data) throw new Error('Unrecognised Judge.me response');
        return data;
      });
  };

  /* ---------- Filtering helpers ---------- */
  Controller.prototype.hasFilters = function () {
    return !!(this.state.rating || this.state.media || this.state.search);
  };

  Controller.prototype.matches = function (r) {
    var s = this.state;
    if (s.rating && Math.round(r.rating) !== Number(s.rating)) return false;
    if (s.media && !(r.pictures.length || r.videos.length)) return false;
    if (s.search) {
      var hay = (r.title + ' ' + plainText(r.bodyHtml) + ' ' + r.author + ' ' + r.answers.map(function (a) { return a.value; }).join(' ')).toLowerCase();
      var terms = s.search.toLowerCase().split(/\s+/).filter(Boolean);
      for (var i = 0; i < terms.length; i++) if (hay.indexOf(terms[i]) === -1) return false;
    }
    return true;
  };

  var SORT_CHECKS = {
    latest: function (a, b) { return new Date(b.createdAt) - new Date(a.createdAt); },
    oldest: function (a, b) { return new Date(a.createdAt) - new Date(b.createdAt); },
    highest: function (a, b) { return b.rating - a.rating; },
    lowest: function (a, b) { return a.rating - b.rating; },
    helpful: function (a, b) { return b.up - a.up; },
    pictures: function (a, b) { return (b.pictures.length || b.videos.length ? 1 : 0) - (a.pictures.length || a.videos.length ? 1 : 0); }
  };

  /** If the feed ignored our sort parameter, sort each batch on the client instead. */
  Controller.prototype.applySort = function (reviews) {
    var cmp = SORT_CHECKS[this.state.sort];
    if (!cmp || (!this.sortTouched && this.initialSortUnknown)) return reviews;
    if (!this.state.sortVerified && reviews.length > 1) {
      var ordered = reviews.every(function (r, i) { return i === 0 || cmp(reviews[i - 1], r) <= 0; });
      this.state.clientSort = !ordered;
      this.state.sortVerified = true;
    }
    return this.state.clientSort ? reviews.slice().sort(cmp) : reviews;
  };

  /* ---------- Loading ---------- */
  Controller.prototype.start = function () {
    var init = this.initial;
    if (init) {
      this.initialSortKey = init.sortKey || '';
      this.syncSortSelect();
      this.applySummary(init);
    }
    var canUseInitial = init && init.reviews && !this.state.perPage;
    if (canUseInitial) {
      this.state.serverPage = 1;
      this.state.totalPages = init.totalPages;
      this.state.pageSize = init.perPage || init.reviews.length;
      this.append(init.reviews, true);
      this.state.done = this.computeDone(init, 1);
      this.renderFooter();
      this.revealToolbar();
    } else {
      this.reload();
    }
  };

  Controller.prototype.syncSortSelect = function () {
    if (!this.el.sort || !this.initialSortKey) return;
    var key = this.initialSortKey;
    for (var name in SORTS) {
      if (SORTS[name].sort_by === key || name === key) {
        this.el.sort.value = name;
        this.state.sort = name;
        return;
      }
    }
    this.initialSortUnknown = true;
  };

  Controller.prototype.computeDone = function (data, page) {
    if (!data.reviews.length) return true;
    if (isFinite(data.totalPages)) return page >= data.totalPages;
    var size = this.state.pageSize || data.perPage;
    return size ? data.reviews.length < size : false;
  };

  Controller.prototype.reload = function () {
    var s = this.state;
    s.reviews = [];
    s.ids = Object.create(null);
    s.serverPage = 0;
    s.done = false;
    s.scan = false;
    s.sortVerified = false;
    s.clientSort = false;
    this.el.list.innerHTML = this.skeleton(2);
    this.setStatus(this.L.loading || '');
    this.loadMore(true);
  };

  Controller.prototype.loadMore = function (isReset) {
    var self = this;
    var s = this.state;
    if (s.loading && !isReset) return;
    if (s.done && !isReset) return;
    s.loading = true;
    var reqId = ++this.requestId;
    this.el.list.setAttribute('aria-busy', 'true');
    if (this.el.more) this.el.more.disabled = true;

    var job = s.scan ? this.loadScan(reqId) : this.loadServer(reqId);
    job
      .then(function () {
        if (reqId !== self.requestId) return;
        self.setStatus('');
        self.renderFooter();
        self.revealToolbar();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (reqId !== self.requestId) return;
        console.warn('[Custom Reviews]', err);
        if (isReset) self.el.list.innerHTML = '';
        self.renderError();
      })
      .then(function () {
        if (reqId !== self.requestId) return;
        s.loading = false;
        self.el.list.removeAttribute('aria-busy');
        if (self.el.more) self.el.more.disabled = false;
      });
  };

  /** Normal path: the feed does pagination, sorting and filtering. */
  Controller.prototype.loadServer = function (reqId) {
    var self = this;
    var s = this.state;
    var page = s.serverPage + 1;
    return this.fetchPage(page).then(function (data) {
      if (reqId !== self.requestId) return;
      if (!self.summary) self.applySummary(data);
      s.pageSize = s.pageSize || data.perPage || data.reviews.length;
      if (self.hasFilters() && !data.reviews.every(self.matches, self)) {
        // The feed ignored at least one filter → switch to client-side scanning.
        s.scan = true;
        s.serverPage = 0;
        if (page === 1) self.el.list.innerHTML = '';
        return self.loadScan(reqId, data, page);
      }
      s.serverPage = page;
      s.totalPages = data.totalPages;
      if (page === 1) self.el.list.innerHTML = '';
      self.append(self.applySort(data.reviews));
      s.done = self.computeDone(data, page);
    });
  };

  /** Fallback path: walk through pages and keep only matching reviews. */
  Controller.prototype.loadScan = function (reqId, firstData, firstPage) {
    var self = this;
    var s = this.state;
    var want = s.pageSize || 5;
    var found = 0;
    var scanned = 0;
    var maxPages = num(this.cfg.maxScanPages) || 15;

    function consume(data, page) {
      s.serverPage = page;
      s.totalPages = data.totalPages;
      var hits = data.reviews.filter(self.matches, self);
      found += self.append(self.applySort(hits));
      if (self.computeDone(data, page)) s.done = true;
    }

    function step() {
      if (reqId !== self.requestId || s.done || found >= want || scanned >= maxPages) return Promise.resolve();
      var page = s.serverPage + 1;
      scanned++;
      return self.fetchPage(page, { unfiltered: true }).then(function (data) {
        if (reqId !== self.requestId) return;
        consume(data, page);
        return step();
      });
    }

    if (firstData && firstPage === 1 && !this.state.rating && !this.state.search) {
      // no filter params were sent, so this first response is a plain page 1 and can be reused
      scanned++;
      consume(firstData, 1);
    }
    return step();
  };

  Controller.prototype.append = function (reviews, initial) {
    var s = this.state;
    var fresh = reviews.filter(function (r) {
      if (s.ids[r.id]) return false;
      s.ids[r.id] = true;
      return true;
    });
    if (initial) this.el.list.innerHTML = '';
    s.reviews = s.reviews.concat(fresh);
    var html = fresh.map(this.reviewHTML, this).join('');
    this.el.list.insertAdjacentHTML('beforeend', html);
    this.enhanceList();
    if (this.cfg.showAttributes) this.renderAttributes(); // also fills in Poor/Great labels from answers
    if (this.galleryFromReviews) this.buildGallery();
    return fresh.length;
  };

  /* ---------- Summary ---------- */
  Controller.prototype.applySummary = function (data) {
    this.summary = data;
    var cfg = this.cfg;
    var avg = isFinite(data.average) ? data.average : num(cfg.metafieldAverage);
    var count = isFinite(data.count) ? data.count : num(first(data.total, cfg.metafieldCount));
    this.totalCount = count;

    var summaryEl = this.root.querySelector('[data-cr-summary]');
    if (summaryEl) summaryEl.hidden = !(count > 0);
    if (isFinite(avg) && count > 0) {
      this.el.average.textContent = formatNumber(avg, 2);
      var fill = this.el.averageStars && this.el.averageStars.querySelector('.cr__stars-fill');
      if (fill) fill.style.width = Math.min(100, (avg / 5) * 100) + '%';
      if (this.el.averageStars) this.el.averageStars.setAttribute('aria-label', formatNumber(avg, 2) + ' / 5');
      this.el.count.textContent = formatNumber(count) + ' ' + (this.L.reviews || '');
    }

    if (cfg.showHistogram) this.renderHistogram(data.histogram, count);
    if (cfg.showAttributes) {
      this.summaryAttrs = data.attributes || [];
      this.needsComputedAttributes = !this.summaryAttrs.length && cfg.attributesFromReviews;
      this.renderAttributes();
    }

    var ai = (cfg.showAi && (data.ai || cfg.aiFallback)) || '';
    if (this.el.ai) {
      this.el.ai.hidden = !ai;
      if (ai) this.el.aiText.textContent = plainText(ai);
    }

    if (cfg.showGallery) {
      this.galleryFromReviews = !(data.gallery && data.gallery.length);
      this.gallerySource = data.gallery || [];
      this.buildGallery();
    }
  };

  Controller.prototype.renderHistogram = function (histogram, count) {
    var box = this.el.histogram;
    if (!box) return;
    if (!histogram || !histogram.length) {
      box.innerHTML = '';
      return;
    }
    var byRating = {};
    histogram.forEach(function (h) { byRating[h.rating] = h; });
    var total = count || histogram.reduce(function (sum, h) { return sum + (h.frequency || 0); }, 0);
    var html = '';
    for (var r = 5; r >= 1; r--) {
      var h = byRating[r] || { frequency: 0, percentage: 0 };
      var pct = isFinite(h.percentage) ? h.percentage : total ? (h.frequency / total) * 100 : 0;
      var label = r + ' ★ — ' + formatNumber(h.frequency || 0) + ' ' + (this.L.reviews || '') + ' (' + Math.round(pct) + '%)';
      html +=
        '<button type="button" class="cr__hist-row" data-cr-hist="' + r + '" aria-pressed="' + (String(this.state.rating) === String(r)) + '" aria-label="' + esc(label) + '" title="' + esc(label) + '">' +
        '<span class="cr__hist-label">' + r + '</span>' +
        '<span class="cr__hist-bar"><span class="cr__hist-fill" ' + (h.frequency ? '' : 'data-zero ') + 'style="width:' + Math.max(0, Math.min(100, pct)) + '%"></span></span>' +
        '</button>';
    }
    box.innerHTML = html;
  };

  Controller.prototype.computedAttributes = function () {
    var groups = {};
    var order = [];
    this.state.reviews.forEach(function (r) {
      r.answers.forEach(function (a) {
        if (!isScale(a)) return;
        var key = a.title.toLowerCase();
        if (!groups[key]) {
          groups[key] = { title: a.title, lower: a.lower, upper: a.upper, sum: 0, n: 0 };
          order.push(key);
        }
        groups[key].sum += num(a.value);
        groups[key].n++;
      });
    });
    return order.map(function (k) {
      var g = groups[k];
      return { title: g.title, lower: g.lower, upper: g.upper, value: g.sum / g.n };
    });
  };

  Controller.prototype.labelsFor = function (attr) {
    // Borrow left/right labels from any loaded review answer if the summary lacks them.
    if (attr.lower || attr.upper) return attr;
    var t = (attr.title || '').toLowerCase();
    for (var i = 0; i < this.state.reviews.length; i++) {
      var hit = this.state.reviews[i].answers.filter(function (a) {
        return (attr.id != null && a.id == attr.id) || a.title.toLowerCase() === t;
      })[0];
      if (hit && (hit.lower || hit.upper)) return Object.assign({}, attr, { lower: hit.lower, upper: hit.upper });
    }
    return attr;
  };

  Controller.prototype.renderAttributes = function () {
    var box = this.el.attributes;
    if (!box) return;
    var list = this.needsComputedAttributes ? this.computedAttributes() : (this.summaryAttrs || []);
    var self = this;
    box.innerHTML = list.map(function (a) { return self.scaleHTML(self.labelsFor(a), false); }).join('');
  };

  Controller.prototype.scaleHTML = function (attr, small) {
    var points = num(attr.points) || num(this.cfg.scalePoints) || 5;
    var v = num(attr.value);
    if (v > points) points = v <= 5 ? 5 : v <= 7 ? 7 : 10;
    var pos = points > 1 ? ((v - 1) / (points - 1)) * 100 : 0;
    pos = Math.max(0, Math.min(100, pos));
    var segs = '';
    for (var i = 0; i < points - 1; i++) segs += '<span class="cr-scale__seg"></span>';
    var middle = this.cfg.scaleMiddleLabel;
    var labels = '';
    if (attr.lower || attr.upper) {
      labels =
        '<div class="cr-scale__labels" aria-hidden="true"><span>' + esc(attr.lower) + '</span>' +
        (middle ? '<span>' + esc(middle) + '</span>' : '') +
        '<span>' + esc(attr.upper) + '</span></div>';
    }
    var aria = attr.title + ': ' + formatNumber(v, 1) + ' / ' + points + (attr.lower || attr.upper ? ' (' + attr.lower + ' → ' + attr.upper + ')' : '');
    return (
      '<div class="cr-scale' + (small ? ' cr-scale--sm' : '') + '" role="img" aria-label="' + esc(aria) + '">' +
      '<span class="cr-scale__title">' + esc(attr.title) + '</span>' +
      '<div class="cr-scale__track">' + segs + '<span class="cr-scale__marker" style="left:clamp(7px, ' + pos.toFixed(2) + '%, calc(100% - 7px))"></span></div>' +
      labels +
      '</div>'
    );
  };

  /* ---------- Gallery ---------- */
  Controller.prototype.buildGallery = function () {
    var reviews = this.galleryFromReviews ? this.state.reviews : this.gallerySource;
    var items = [];
    var seen = {};
    reviews.forEach(function (r) {
      r.pictures.forEach(function (p) {
        if (seen[p.thumb]) return;
        seen[p.thumb] = 1;
        items.push({ type: 'image', thumb: p.thumb, full: p.full, review: r });
      });
      r.videos.forEach(function (v) {
        if (seen['v' + v.vimeo]) return;
        seen['v' + v.vimeo] = 1;
        items.push({ type: 'video', vimeo: v.vimeo, review: r });
      });
    });
    this.galleryItems = items;
    if (!this.el.gallery) return;
    this.el.gallery.hidden = !items.length;
    this.el.galleryTrack.innerHTML = items.map(function (it, i) {
      if (it.type === 'video') {
        return '<button type="button" class="cr__thumb cr__thumb--video" data-cr-open="gallery" data-index="' + i + '" data-vimeo="' + esc(it.vimeo) + '" aria-label="Video"></button>';
      }
      return '<button type="button" class="cr__thumb" data-cr-open="gallery" data-index="' + i + '"><img src="' + esc(it.thumb) + '" alt="' + esc(it.review.title || it.review.author || '') + '" loading="lazy" decoding="async"></button>';
    }).join('');
    this.loadVideoThumbs(this.el.galleryTrack);
    this.updateGalleryNav();
  };

  Controller.prototype.loadVideoThumbs = function (scope) {
    Array.prototype.slice.call(scope.querySelectorAll('[data-vimeo]:not([data-thumb-loaded])')).forEach(function (btn) {
      btn.setAttribute('data-thumb-loaded', '1');
      var id = btn.getAttribute('data-vimeo');
      fetch('https://vimeo.com/api/oembed.json?url=' + encodeURIComponent('https://vimeo.com/' + id))
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (j && j.thumbnail_url) btn.insertAdjacentHTML('afterbegin', '<img src="' + esc(j.thumbnail_url) + '" alt="" loading="lazy">');
        })
        .catch(function () {});
    });
  };

  Controller.prototype.updateGalleryNav = function () {
    var track = this.el.galleryTrack;
    var g = this.el.gallery;
    if (!track || !g) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    g.toggleAttribute('data-can-prev', track.scrollLeft > 2);
    g.toggleAttribute('data-can-next', track.scrollLeft < max);
  };

  /* ---------- Review rendering ---------- */
  Controller.prototype.reviewHTML = function (r) {
    var L = this.L;
    var cfg = this.cfg;
    var kw = (cfg.recommendKeyword || '').toLowerCase();
    var scales = [];
    var others = [];
    var recommend = null;
    r.answers.forEach(function (a) {
      if (kw && a.title.toLowerCase().indexOf(kw) !== -1 && !isScale(a)) {
        recommend = /^(yes|true|1|y|sí|si|oui|ja)$/i.test(a.value.trim());
        return;
      }
      if (isScale(a)) scales.push(a);
      else if (a.value) others.push(a);
    });

    var verified = r.verified
      ? '<span class="cr-review__verified"><svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.5 8.2l2.2 2.2 4.8-4.8" fill="none" stroke="#fff" stroke-width="1.8"/></svg>' + esc(L.verified) + '</span>'
      : '';

    var bodyLength = plainText(r.bodyHtml).length;
    var clamp = bodyLength > 600;

    var pics = '';
    if (r.pictures.length || r.videos.length) {
      pics = '<div class="cr-review__pics">' +
        r.pictures.map(function (p, i) {
          return '<button type="button" class="cr__thumb" data-cr-open="review" data-review="' + esc(r.id) + '" data-index="' + i + '"><img src="' + esc(p.thumb) + '" alt="" loading="lazy" decoding="async"></button>';
        }).join('') +
        r.videos.map(function (v, i) {
          return '<button type="button" class="cr__thumb cr__thumb--video" data-cr-open="review" data-review="' + esc(r.id) + '" data-index="' + (r.pictures.length + i) + '" data-vimeo="' + esc(v.vimeo) + '" aria-label="Video"></button>';
        }).join('') +
        '</div>';
    }

    var attrs = cfg.showReviewAttributes && scales.length
      ? '<div class="cr-review__attrs">' + scales.map(function (a) { return this.scaleHTML(a, true); }, this).join('') + '</div>'
      : '';

    var answers = cfg.showReviewAttributes && others.length
      ? '<div class="cr-review__answers">' + others.map(function (a) { return '<span><b>' + esc(a.title) + ':</b> ' + esc(a.value) + '</span>'; }).join('') + '</div>'
      : '';

    var productName = r.productTitle || cfg.productTitle || '';
    var productLine = cfg.showProductLine && (r.variant || r.productTitle)
      ? '<div class="cr-review__product">' + esc(L.reviewFor) + ' ' + esc(productName) + (r.variant ? ' - ' + esc(r.variant) : '') + '</div>'
      : '';

    var reply = cfg.showReplies && r.reply && r.reply.html
      ? '<div class="cr-review__reply"><div class="cr-review__reply-name">' + esc(r.reply.name) + (r.reply.name ? ' ' : '') + esc(L.reply) + '</div>' + r.reply.html + '</div>'
      : '';

    var helpful = cfg.showHelpful && (r.up || r.down)
      ? '<div class="cr-review__helpful" aria-label="' + esc(L.helpful) + '">' + esc(L.helpful) +
        '<span title="👍"><svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true"><path d="M7 1l6 10H1z" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>' + r.up + '</span>' +
        '<span class="cr-sep"></span>' +
        '<span title="👎"><svg width="14" height="12" viewBox="0 0 14 12" aria-hidden="true"><path d="M7 11L1 1h12z" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>' + r.down + '</span></div>'
      : '';

    var rec = recommend === true
      ? '<span class="cr-review__recommend"><svg width="13" height="10" viewBox="0 0 13 10" aria-hidden="true"><path d="M1 5l3.5 3.5L12 1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' + esc(L.recommend) + '</span>'
      : '';

    var footer = rec || helpful ? '<div class="cr-review__footer">' + (rec || '<span></span>') + helpful + '</div>' : '';
    var author = r.author || L.anonymous || '';

    return (
      '<li class="cr-review" data-review-id="' + esc(r.id) + '">' +
        '<div class="cr-review__author">' +
          '<span class="cr-review__avatar" aria-hidden="true">' + esc(r.initials || initialsOf(author)) + '</span>' +
          '<div class="cr-review__who"><div class="cr-review__name">' + esc(author) + '</div>' + verified +
          (r.location ? '<span class="cr-review__location">' + esc(r.location) + '</span>' : '') + '</div>' +
        '</div>' +
        '<div class="cr-review__body-wrap">' +
          '<div class="cr-review__stars">' + starsHTML(r.rating, 'cr__stars--md') + '</div>' +
          (r.createdAt ? '<time class="cr-review__date" datetime="' + esc(r.createdAt) + '" title="' + esc(new Date(r.createdAt).toLocaleString(lang())) + '">' + formatDate(r.createdAt, cfg.relativeDates) + '</time>' : '') +
          (r.title ? '<h3 class="cr-review__title">' + esc(r.title) + '</h3>' : '<div style="height:14px"></div>') +
          '<div class="cr-review__body"' + (clamp ? ' data-clamped' : '') + '>' + r.bodyHtml + '</div>' +
          (clamp ? '<button type="button" class="cr-review__readmore" data-cr-readmore>' + esc(L.readMore) + '</button>' : '') +
          pics + attrs + answers + productLine + reply + footer +
        '</div>' +
      '</li>'
    );
  };

  Controller.prototype.enhanceList = function () {
    this.loadVideoThumbs(this.el.list);
  };

  Controller.prototype.skeleton = function (n) {
    var one = '<li class="cr-skel" aria-hidden="true"><div><i style="width:60%"></i><i style="width:40%"></i></div><div><i style="width:30%"></i><i style="width:90%"></i><i style="width:75%"></i></div></li>';
    return new Array(n + 1).join(one);
  };

  Controller.prototype.renderFooter = function () {
    var s = this.state;
    if (this.el.more) this.el.more.hidden = s.done || !s.reviews.length && s.done;
    if (!s.reviews.length) {
      if (s.done || !this.hasFilters()) {
        this.el.list.innerHTML = '<li class="cr__empty">' + esc(this.hasFilters() ? this.L.noMatch : this.L.empty) + '</li>';
      } else {
        // scanned the max pages without a hit, more pages exist
        this.el.list.innerHTML = '<li class="cr__empty">' + esc(this.L.noMatch) + '</li>';
      }
    }
    if (!this.hasFilters() && s.reviews.length && this.totalCount > 0 && this.L.showing) {
      this.setStatus(this.L.showing.replace('{shown}', formatNumber(s.reviews.length)).replace('{total}', formatNumber(this.totalCount)));
    }
  };

  Controller.prototype.renderError = function () {
    var self = this;
    this.setStatus('');
    this.el.status.innerHTML = esc(this.L.error) + '<button type="button">' + esc(this.L.retry) + '</button>';
    this.el.status.querySelector('button').addEventListener('click', function () {
      if (self.state.reviews.length) self.loadMore(); else self.reload();
    });
  };

  Controller.prototype.setStatus = function (text) {
    this.el.status.textContent = text || '';
  };

  Controller.prototype.revealToolbar = function () {
    if (this.el.toolbar && (this.state.reviews.length || this.hasFilters() || this.totalCount > 0)) this.el.toolbar.hidden = false;
  };

  /* ---------- Lightbox ---------- */
  Controller.prototype.openLightbox = function (items, index) {
    var dlg = this.el.lightbox;
    if (!dlg || !items.length) return;
    this.lb = { items: items, index: index };
    this.renderLightbox();
    if (typeof dlg.showModal === 'function') dlg.showModal();
    else dlg.setAttribute('open', '');
  };

  Controller.prototype.renderLightbox = function () {
    var lb = this.lb;
    var it = lb.items[lb.index];
    var r = it.review;
    this.el.lightboxMedia.innerHTML = it.type === 'video'
      ? '<iframe src="https://player.vimeo.com/video/' + encodeURIComponent(it.vimeo) + '?autoplay=1" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Video"></iframe>'
      : '<img src="' + esc(it.full || it.thumb) + '" alt="' + esc(r.title || '') + '">';
    this.el.lightboxReview.innerHTML =
      '<div class="cr-review__author"><span class="cr-review__avatar" aria-hidden="true">' + esc(r.initials || initialsOf(r.author)) + '</span>' +
      '<div class="cr-review__who"><div class="cr-review__name">' + esc(r.author || this.L.anonymous) + '</div>' +
      (r.verified ? '<span class="cr-review__verified">' + esc(this.L.verified) + '</span>' : '') + '</div></div>' +
      '<div style="margin-top:14px">' + starsHTML(r.rating, 'cr__stars--sm') + '</div>' +
      (r.title ? '<h3 class="cr-review__title">' + esc(r.title) + '</h3>' : '') +
      '<div class="cr-review__body">' + r.bodyHtml + '</div>' +
      (r.createdAt ? '<p class="cr-review__location" style="margin-top:14px">' + formatDate(r.createdAt, this.cfg.relativeDates) + '</p>' : '');
    var many = lb.items.length > 1;
    this.el.lightboxPrev.hidden = !many;
    this.el.lightboxNext.hidden = !many;
  };

  Controller.prototype.stepLightbox = function (delta) {
    if (!this.lb) return;
    var n = this.lb.items.length;
    this.lb.index = (this.lb.index + delta + n) % n;
    this.renderLightbox();
  };

  Controller.prototype.closeLightbox = function () {
    var dlg = this.el.lightbox;
    this.el.lightboxMedia.innerHTML = '';
    if (dlg.open && typeof dlg.close === 'function') dlg.close();
    else dlg.removeAttribute('open');
  };

  /* ---------- Judge.me native form ("Write a review") ---------- */
  Controller.prototype.nativeWidgets = function () {
    var sel = this.cfg.nativeWidget;
    if (!sel) return [];
    var root = this.root;
    try {
      return Array.prototype.slice.call(document.querySelectorAll(sel)).filter(function (el) {
        return !root.contains(el) && !el.contains(root);
      });
    } catch (e) {
      return [];
    }
  };

  Controller.prototype.hideNativeWidgets = function () {
    if (!this.cfg.hideNative) return;
    this.nativeWidgets().forEach(function (el) {
      if (!el.classList.contains('cr-native-form-open')) el.classList.add('cr-native-hidden');
    });
  };

  /** Judge.me's own "Write a review" link, looked up inside its widget first. */
  Controller.prototype.findNativeTrigger = function (widgets) {
    var sel = this.cfg.nativeTrigger;
    if (!sel) return null;
    var root = this.root;
    var scopes = (widgets || []).concat(document);
    try {
      for (var i = 0; i < scopes.length; i++) {
        var hit = Array.prototype.slice.call(scopes[i].querySelectorAll(sel)).filter(function (el) {
          return !root.contains(el);
        })[0];
        if (hit) return hit;
      }
    } catch (e) {}
    return null;
  };

  /**
   * Judge.me lazy-loads its review form: the first click on its "Write a review" link only
   * downloads form.js, and the form opens on a click made after form.js has set it up.
   * So: wait for Judge.me to boot, let it load the form, wait for the form, then open it.
   */
  Controller.prototype.openWriteForm = function () {
    var self = this;
    if (this.writePending) return;
    var widgets = this.nativeWidgets();
    if (!widgets.length) return this.writeFallback();
    var widget = widgets[0];

    this.writePending = true;
    if (this.el.write) this.el.write.setAttribute('aria-busy', 'true');
    this.showNativeForm(widgets);

    function formReady() {
      return widget.querySelector('.jdgm-form-wrapper') || document.querySelector('.jdgm-write-review-modal');
    }

    // Judge.me sets jdgm._doneSetup once its widget scripts have bound their handlers.
    waitFor(function (elapsed) {
      var ready = window.jdgm && (window.jdgm._doneSetup || elapsed > 4000);
      return ready && self.findNativeTrigger(widgets);
    }, 15000)
      .then(function (trigger) {
        if (formReady()) return trigger;
        trigger.click(); // makes Judge.me download and build its form
        return waitFor(function () { return formReady() && self.findNativeTrigger(widgets); }, 15000);
      })
      .then(function (trigger) {
        if (trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
        self.scrollToForm(widget);
      })
      .catch(function () {
        self.writeFallback();
      })
      .then(function () {
        self.writePending = false;
        if (self.el.write) self.el.write.removeAttribute('aria-busy');
      });
  };

  Controller.prototype.showNativeForm = function (widgets) {
    var $ = window.jdgm && window.jdgm.$;
    widgets.forEach(function (w) {
      w.classList.remove('cr-native-hidden');
      w.classList.add('cr-native-form-open');
      // Judge.me hides its widget on products with no reviews when web reviews are turned off.
      if ($ && window.jdgm.forceShowHideRevWidget) window.jdgm.forceShowHideRevWidget($(w), true);
    });
  };

  Controller.prototype.scrollToForm = function (widget) {
    // Judge.me scrolls to its form too; only step in if the form ended up off screen.
    setTimeout(function () {
      var form = widget.querySelector('.jdgm-form-wrapper');
      if (!form || form.offsetParent === null) return;
      var top = form.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        window.scrollTo({ top: window.pageYOffset + top - 120, behavior: 'smooth' });
      }
    }, 700);
  };

  Controller.prototype.writeFallback = function () {
    this.nativeWidgets().forEach(function (w) { w.classList.remove('cr-native-form-open'); });
    this.hideNativeWidgets();
    if (this.cfg.writeUrl) {
      window.location.href = this.cfg.writeUrl;
    } else {
      console.warn('[Custom Reviews] Judge.me review form not found. Add the Judge.me Review Widget app block to this template, or set a fallback link in the section settings.');
    }
  };

  /* ---------- Events ---------- */
  Controller.prototype.bind = function () {
    var self = this;
    var el = this.el;
    var root = this.root;

    if (el.write) el.write.addEventListener('click', function () { self.openWriteForm(); });
    if (el.more) el.more.addEventListener('click', function () { self.loadMore(); });

    if (el.sort) el.sort.addEventListener('change', function () {
      self.state.sort = el.sort.value;
      self.sortTouched = true;
      self.reload();
    });
    if (el.rating) el.rating.addEventListener('change', function () {
      self.setRating(el.rating.value);
    });
    if (el.media) el.media.addEventListener('change', function () {
      self.state.media = el.media.checked;
      self.reload();
    });
    if (el.search) {
      var t;
      el.search.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          var q = el.search.value.trim();
          if (q.length === 1 || q === self.state.search) return;
          self.state.search = q;
          self.reload();
        }, 400);
      });
    }

    root.addEventListener('click', function (e) {
      var hist = e.target.closest('[data-cr-hist]');
      if (hist) {
        var r = hist.getAttribute('data-cr-hist');
        self.setRating(String(self.state.rating) === r ? '' : r);
        return;
      }
      var open = e.target.closest('[data-cr-open]');
      if (open) {
        var idx = Number(open.getAttribute('data-index')) || 0;
        if (open.getAttribute('data-cr-open') === 'gallery') {
          self.openLightbox(self.galleryItems, idx);
        } else {
          var id = open.getAttribute('data-review');
          var review = self.state.reviews.filter(function (x) { return x.id === id; })[0];
          if (review) {
            var items = review.pictures.map(function (p) { return { type: 'image', thumb: p.thumb, full: p.full, review: review }; })
              .concat(review.videos.map(function (v) { return { type: 'video', vimeo: v.vimeo, review: review }; }));
            self.openLightbox(items, idx);
          }
        }
        return;
      }
      var more = e.target.closest('[data-cr-readmore]');
      if (more) {
        var body = more.previousElementSibling;
        var expanded = !body.hasAttribute('data-clamped');
        if (expanded) body.setAttribute('data-clamped', ''); else body.removeAttribute('data-clamped');
        more.hidden = !expanded;
      }
    });

    if (el.galleryTrack) {
      el.galleryTrack.addEventListener('scroll', function () { self.updateGalleryNav(); }, { passive: true });
      window.addEventListener('resize', function () { self.updateGalleryNav(); });
      var scrollBy = function (dir) {
        el.galleryTrack.scrollBy({ left: dir * el.galleryTrack.clientWidth * 0.8, behavior: 'smooth' });
      };
      if (el.galleryPrev) el.galleryPrev.addEventListener('click', function () { scrollBy(-1); });
      if (el.galleryNext) el.galleryNext.addEventListener('click', function () { scrollBy(1); });
    }

    if (el.lightbox) {
      el.lightboxClose.addEventListener('click', function () { self.closeLightbox(); });
      el.lightboxPrev.addEventListener('click', function () { self.stepLightbox(-1); });
      el.lightboxNext.addEventListener('click', function () { self.stepLightbox(1); });
      el.lightbox.addEventListener('click', function (e) { if (e.target === el.lightbox) self.closeLightbox(); });
      el.lightbox.addEventListener('close', function () { el.lightboxMedia.innerHTML = ''; });
      el.lightbox.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') self.stepLightbox(-1);
        if (e.key === 'ArrowRight') self.stepLightbox(1);
      });
    }

    // Re-hide Judge.me's widget when its inline form is cancelled (legacy widget).
    document.addEventListener('click', function (e) {
      if (!self.cfg.hideNative) return;
      if (e.target.closest && e.target.closest('.jdgm-cancel-rev, .jdgm-cancel-rev-btn')) {
        self.nativeWidgets().forEach(function (w) {
          w.classList.remove('cr-native-form-open');
          w.classList.add('cr-native-hidden');
        });
      }
    });
  };

  Controller.prototype.setRating = function (value) {
    this.state.rating = value || '';
    if (this.el.rating) this.el.rating.value = this.state.rating;
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-cr-hist]'), function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-cr-hist') === String(self.state.rating)));
    });
    this.reload();
  };

  Controller.prototype.destroy = function () {
    if (this.abort) this.abort.abort();
    this.requestId++;
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-custom-reviews]'), function (root) {
      if (!root.__customReviews) new Controller(root);
    });
  }

  window.CustomReviews = {
    initAll: initAll,
    Controller: Controller,
    parsePayload: parsePayload,
    PARAMS: PARAMS,
    SORTS: SORTS
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-custom-reviews]'), function (root) {
      if (root.__customReviews) root.__customReviews.destroy();
    });
  });
  // Judge.me may inject its widget after we boot — keep it hidden.
  window.addEventListener('load', function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-custom-reviews]'), function (root) {
      if (root.__customReviews) root.__customReviews.hideNativeWidgets();
    });
  });
})();
